import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Grid,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import Papa from 'papaparse';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ReportsAnalytics = () => {
  const [reports, setReports] = useState({});
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [financialData, setFinancialData] = useState([]);
  const [financialLoading, setFinancialLoading] = useState(false);
  const { adminUser } = useContext(AuthContext);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      if (!adminUser) return;
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        const res = await api.get('/admin/states', config);
        setStates(res.data);
      } catch (err) {
        console.error('States fetch error:', err.response?.data);
      }
    };
    fetchStates();
  }, [adminUser]);

  // Fetch cities based on selected state
  useEffect(() => {
    if (selectedState) {
      const fetchCities = async () => {
        const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
        try {
          const res = await api.get(`/admin/cities?state=${selectedState}`, config);
          setCities(res.data);
        } catch (err) {
          console.error('Cities fetch error:', err.response?.data);
        }
      };
      fetchCities();
    } else {
      setCities([]);
    }
  }, [selectedState, adminUser]);

  // Fetch reports
  useEffect(() => {
    const fetchReports = async () => {
      if (!adminUser) return;
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      const params = {};
      if (selectedState) params.state = selectedState;
      if (selectedCity) params.city = selectedCity;
      if (dateRange.start) params.startDate = dateRange.start.toISOString();
      if (dateRange.end) params.endDate = dateRange.end.toISOString();

      try {
        const res = await api.get('/admin/reports', { ...config, params });
        setReports(res.data);
      } catch (err) {
        console.error('Reports fetch error:', err.response?.data);
      }
    };
    fetchReports();
  }, [adminUser, selectedState, selectedCity, dateRange]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!adminUser) return;
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      const params = {};
      if (selectedState) params.state = selectedState;
      if (selectedCity) params.city = selectedCity;
  
      try {
        const res = await api.get('/admin/users', { ...config, params });
        const formattedDoctors = res.data.doctors.map((doctor) => ({
          ...doctor,
          pendingPayout: doctor.pendingPayout || 0, // Add pending payout field
        }));
        setDoctors(formattedDoctors);
        setPatients(res.data.patients);
      } catch (err) {
        console.error('Users fetch error:', err.response?.data);
      }
    };
    fetchUsers();
  }, [adminUser, selectedState, selectedCity]);

  // Fetch appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!adminUser) return;
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      const params = {};
      if (selectedState) params.state = selectedState;
      if (selectedCity) params.city = selectedCity;
      if (dateRange.start) params.startDate = dateRange.start.toISOString();
      if (dateRange.end) params.endDate = dateRange.end.toISOString();

      try {
        const res = await api.get('/admin/appointments', { ...config, params });
        const formattedAppointments = res.data.map((appt) => ({
          ...appt,
          id: appt._id,
          patientName: appt.patientId?.name || 'N/A',
          doctorName: appt.doctorId?.name || 'N/A',
        }));
        setAppointments(formattedAppointments);
      } catch (err) {
        console.error('Appointments fetch error:', err.response?.data);
      }
    };
    fetchAppointments();
  }, [adminUser, selectedState, selectedCity, dateRange]);

  // Fetch withdrawals
  useEffect(() => {
    const fetchWithdrawals = async () => {
      if (!adminUser) return;
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      const params = {};
      if (selectedState) params.state = selectedState;
      if (selectedCity) params.city = selectedCity;

      try {
        const res = await api.get('/admin/withdrawals', { ...config, params });
        setWithdrawals(res.data);
      } catch (err) {
        console.error('Withdrawals fetch error:', err.response?.data);
      }
    };
    fetchWithdrawals();
  }, [adminUser, selectedState, selectedCity]);

  // Fetch financial data
  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!adminUser) return;
      setFinancialLoading(true);
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      const params = {};
      if (selectedState) params.state = selectedState;
      if (selectedCity) params.city = selectedCity;
      if (dateRange.start) params.startDate = dateRange.start.toISOString();
      if (dateRange.end) params.endDate = dateRange.end.toISOString();

      try {
        const res = await api.get('/admin/earnings-payments', { ...config, params });
        setFinancialData(res.data.breakdown || []);
      } catch (err) {
        console.error('Financial data fetch error:', err.response?.data);
      } finally {
        setFinancialLoading(false);
      }
    };
    fetchFinancialData();
  }, [adminUser, selectedState, selectedCity, dateRange]);

  const handleExport = (data, filename) => {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleQuickDateFilter = (filter) => {
    const now = new Date();
    let start, end;

    switch (filter) {
      case 'last7Days':
        start = new Date(now.setDate(now.getDate() - 7));
        end = new Date();
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      case 'lastYear':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
      default:
        start = null;
        end = null;
    }
    setDateRange({ start, end });
  };

  // DataGrid Columns
  const doctorColumns = [
    { field: 'name', headerName: 'Doctor Name', width: 140 },
    { field: 'specialization', headerName: 'Specialty', width: 110 },
    { field: 'consultationFee', headerName: 'Fee', width: 90 },
    { field: 'appointmentCount', headerName: 'Appts', width: 90 },
    { field: 'completedAppointmentCount', headerName: 'Completed', width: 90 },
    { field: 'totalEarnings', headerName: 'Gross', width: 110, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'finalEarnings', headerName: 'After Comm.', width: 110, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'pendingPayout', headerName: 'Pending Payout (₹)', width: 110, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'isVerified', headerName: 'Verified', width: 90, renderCell: (params) => (params.value ? '✅' : '❌') },
    { field: 'accountStatus', headerName: 'Status', width: 90, renderCell: (params) => (params.value === 'active' ? '🟢' : '🔴') },
    { field: 'state', headerName: 'State', width: 90 },
    { field: 'city', headerName: 'City', width: 90 },
  ];

  const patientColumns = [
    { field: 'name', headerName: 'Patient Name', width: 140 },
    { field: 'email', headerName: 'Email', width: 140 },
    { field: 'phone', headerName: 'Phone', width: 110 },
    { field: 'appointmentCount', headerName: 'Appts', width: 90 },
    { field: 'lastConsultationDate', headerName: 'Last Consult', width: 110 },
    { field: 'createdAt', headerName: 'Created', width: 110 },
    { field: 'state', headerName: 'State', width: 90 },
    { field: 'city', headerName: 'City', width: 90 },
  ];

  const appointmentColumns = [
    { field: 'id', headerName: 'Appt ID', width: 140 },
    { field: 'patientName', headerName: 'Patient', width: 140 },
    { field: 'doctorName', headerName: 'Doctor', width: 140 },
    { field: 'date', headerName: 'Date & Time', width: 140 },
    {
      field: 'status',
      headerName: 'Status',
      width: 90,
      renderCell: (params) => {
        if (params.value === 'completed') return '✅';
        if (params.value === 'pending') return '🟡';
        if (params.value === 'canceled') return '❌';
        return params.value;
      },
    },
    { field: 'paymentStatus', headerName: 'Payment', width: 90, renderCell: (params) => (params.value === 'paid' ? '✅' : '❌') },
  ];

  const withdrawalColumns = [
    { field: 'doctorName', headerName: 'Doctor Name', width: 140 },
    { field: 'amount', headerName: 'Amount', width: 90 },
    { field: 'requestDate', headerName: 'Request Date', width: 110 },
    { field: 'status', headerName: 'Status', width: 90 },
    { field: 'state', headerName: 'State', width: 90 },
    { field: 'city', headerName: 'City', width: 90 },
  ];

  const topEarningDoctorsColumns = [
    { field: 'name', headerName: 'Doctor Name', width: 200 },
    { field: 'specialization', headerName: 'Specialty', width: 150 },
    { field: 'netEarnings', headerName: 'Net Earnings', width: 150, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
  ];
  
  const topSpecialtiesColumns = [
    { field: 'specialty', headerName: 'Specialty', width: 200 },
    { field: 'netDoctorEarnings', headerName: 'Net Doctor Earnings', width: 150, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` }, // Updated from 'revenue' to 'netDoctorEarnings'
  ];

  const financialColumns = [
    { field: 'doctorName', headerName: 'Doctor Name', width: 150 },
    { field: 'state', headerName: 'State', width: 100 },
    { field: 'city', headerName: 'City', width: 100 },
    { field: 'totalPaid', headerName: 'Total Paid', width: 120, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'doctorFee', headerName: 'Doctor Fee', width: 120, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'patientCommission', headerName: 'Patient Comm.', width: 120, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'doctorCommission', headerName: 'Doctor Comm.', width: 120, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'finalDoctorEarning', headerName: 'Net Doctor Earnings', width: 150, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'platformEarning', headerName: 'Platform Earnings', width: 150, renderCell: (params) => `₹${(params.value || 0).toLocaleString('en-IN')}` },
    { field: 'date', headerName: 'Date', width: 150, renderCell: (params) => new Date(params.value).toLocaleDateString() },
  ];

  // Calculate Top Earning Doctors from doctors state
  const topEarningDoctors = doctors
    .map(doctor => ({
      _id: doctor._id,
      name: doctor.name,
      specialization: doctor.specialization,
      netEarnings: doctor.finalEarnings || 0, // Use finalEarnings (net after commission)
    }))
    .sort((a, b) => b.netEarnings - a.netEarnings)
    .slice(0, 10); // Top 10

  // Calculate Top Earning Specialties from appointments and financialData
  const specialtyEarningsMap = {};
  appointments.forEach(app => {
    if (app.status === 'completed' && app.doctorId) {
      const specialty = app.doctorId.specialization || 'Unknown';
      const netDoctorEarning = financialData.find(fd => fd.doctorId === app.doctorId._id && new Date(fd.date).toISOString().split('T')[0] === new Date(app.completedAt).toISOString().split('T')[0])?.finalDoctorEarning || app.consultationFee * (1 - (app.doctorCommissionRate / 100 || 0.2));
      specialtyEarningsMap[specialty] = (specialtyEarningsMap[specialty] || 0) + netDoctorEarning;
    }
  });

  const topSpecialties = Object.entries(specialtyEarningsMap)
    .map(([specialty, netDoctorEarnings]) => ({ specialty, netDoctorEarnings }))
    .sort((a, b) => b.netDoctorEarnings - a.netDoctorEarnings)
    .slice(0, 5); // Top 5 specialties

  // Chart Data
  const revenueChartData = {
    labels: reports.monthlyRevenue?.map((item) => item.month) || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Monthly Revenue',
      data: reports.monthlyRevenue?.map((item) => item.revenue) || [12000, 19000, 3000, 5000, 2000, 3000, 8000, 12000, 15000, 9000, 11000, 14000],
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 1,
    }],
  };

  const appointmentTrendChartData = {
    labels: reports.monthlyAppointments?.map((item) => item.month) || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Appointments',
      data: reports.monthlyAppointments?.map((item) => item.count) || [65, 59, 80, 81, 56, 55, 40, 70, 90, 85, 75, 60],
      fill: false,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1,
    }],
  };

  const revenueGrowthTrendData = {
    labels: reports.revenueGrowth?.map((item) => item.month) || Array.from({ length: 24 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (23 - i));
      return date.toLocaleString('default', { month: 'short', year: 'numeric' });
    }),
    datasets: [{
      label: 'Revenue Growth',
      data: reports.revenueGrowth?.map((item) => item.revenue) || Array.from({ length: 24 }, () => Math.floor(Math.random() * 20000)),
      backgroundColor: 'rgba(54, 162, 235, 0.2)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1,
    }],
  };

  const doctorEarningsComparisonData = {
    labels: topEarningDoctors.map(doc => doc.name), // Use calculated topEarningDoctors
    datasets: [{
      label: 'Net Earnings',
      data: topEarningDoctors.map(doc => doc.netEarnings || 0), // Use netEarnings
      backgroundColor: 'rgba(255, 99, 132, 0.2)',
      borderColor: 'rgba(255, 99, 132, 1)',
      borderWidth: 1,
    }],
  };

  const refundCancellationTrendData = {
    labels: reports.refundCancellationTrend?.map((item) => item.month) || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Cancellations',
        data: reports.refundCancellationTrend?.map((item) => item.cancellations) || Array.from({ length: 12 }, () => Math.floor(Math.random() * 50)),
        borderColor: 'rgb(255, 99, 132)',
        tension: 0.1,
      },
      {
        label: 'Refunds (₹)',
        data: reports.refundCancellationTrend?.map((item) => item.refunds) || Array.from({ length: 12 }, () => Math.floor(Math.random() * 5000)),
        borderColor: 'rgb(54, 162, 235)',
        tension: 0.1,
      },
    ],
  };

// Chart Data Preparation (only updating paymentModeUsageData)
const paymentModeUsageData = {
  labels: ['UPI', 'Credit/Debit Card', 'Net Banking', 'Wallets'],
  datasets: [{
    data: [
      reports.paymentModeBreakdown?.UPI || 0,
      reports.paymentModeBreakdown?.['Credit/Debit Card'] || 0,
      reports.paymentModeBreakdown?.['Net Banking'] || 0,
      reports.paymentModeBreakdown?.Wallets || 0,
    ],
    backgroundColor: [
      'rgba(255, 99, 132, 0.6)',
      'rgba(54, 162, 235, 0.6)',
      'rgba(75, 192, 192, 0.6)',
      'rgba(255, 206, 86, 0.6)',
    ],
    borderWidth: 1,
  }],
};

  const geographicalPerformanceData = {
    labels: (reports.topRevenueStates || []).map((state) => state.name),
    datasets: [{
      label: 'Revenue by State',
      data: (reports.topRevenueStates || []).map((state) => state.revenue),
      backgroundColor: 'rgba(153, 102, 255, 0.2)',
      borderColor: 'rgba(153, 102, 255, 1)',
      borderWidth: 1,
    }],
  };

  const financialChartData = {
    revenueTrend: {
      labels: financialData.map((item) => new Date(item.date).toLocaleDateString()),
      datasets: [
        {
          label: 'Total Revenue',
          data: financialData.map((item) => item.totalPaid),
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
        {
          label: 'Platform Earnings',
          data: financialData.map((item) => item.platformEarning),
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
        {
          label: 'Doctor Earnings',
          data: financialData.map((item) => item.finalDoctorEarning),
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    },
    commissionBreakdown: {
      labels: ['Patient Commission', 'Doctor Commission', 'Platform Earnings'],
      datasets: [{
        data: [
          financialData.reduce((sum, item) => sum + item.patientCommission, 0),
          financialData.reduce((sum, item) => sum + item.doctorCommission, 0),
          financialData.reduce((sum, item) => sum + item.platformEarning, 0),
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
        ],
      }],
    },
    specialtyRevenue: {
      labels: (reports.topSpecialties || []).map((spec) => spec.specialty),
      datasets: [{
        label: 'Revenue by Specialty',
        data: (reports.topSpecialties || []).map((spec) => spec.revenue),
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1,
      }],
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
        <Typography variant="h4" gutterBottom color="primary">
          Reports & Analytics
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <TextField
                select
                label="Select State"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="">All States</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                select
                label="Select City"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                fullWidth
                disabled={!selectedState}
                variant="outlined"
              >
                <MenuItem value="">All Cities</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setSelectedState('');
                  setSelectedCity('');
                  setDateRange({ start: null, end: null });
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
            <Grid item xs={12} lg={6}>
              <DatePicker
                label="Start Date"
                value={dateRange.start}
                onChange={(newValue) => setDateRange({ ...dateRange, start: newValue })}
                renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DatePicker
                label="End Date"
                value={dateRange.end}
                onChange={(newValue) => setDateRange({ ...dateRange, end: newValue })}
                renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="Last 7 Days" onClick={() => handleQuickDateFilter('last7Days')} clickable color="primary" variant="outlined" />
                <Chip label="This Month" onClick={() => handleQuickDateFilter('thisMonth')} clickable color="primary" variant="outlined" />
                <Chip label="Last Month" onClick={() => handleQuickDateFilter('lastMonth')} clickable color="primary" variant="outlined" />
                <Chip label="This Year" onClick={() => handleQuickDateFilter('thisYear')} clickable color="primary" variant="outlined" />
                <Chip label="Last Year" onClick={() => handleQuickDateFilter('lastYear')} clickable color="primary" variant="outlined" />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Overview */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary">Summary Overview</Typography>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ '&:hover': { color: '#1976d2' } }}>
                User Statistics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={4}><Typography variant="body1"><strong>Total Patients:</strong> {reports.totalPatients || 0}</Typography></Grid>
                <Grid item xs={12} lg={4}><Typography variant="body1"><strong>Total Doctors:</strong> {reports.totalDoctors || 0}</Typography></Grid>
                <Grid item xs={12} lg={4}><Typography variant="body1"><strong>Total Active Users (Last 30 days):</strong> {reports.totalActiveUsers || 0}</Typography></Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ '&:hover': { color: '#1976d2' } }}>
                Appointment Statistics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={3}><Typography variant="body1"><strong>Total Appointments:</strong> {reports.totalAppointments || 0}</Typography></Grid>
                <Grid item xs={12} lg={3}><Typography variant="body1"><strong>Completed:</strong> {reports.completedAppointments || 0}</Typography></Grid>
                <Grid item xs={12} lg={3}><Typography variant="body1"><strong>Canceled:</strong> {reports.canceledAppointments || 0}</Typography></Grid>
                <Grid item xs={12} lg={3}><Typography variant="body1"><strong>Rescheduled:</strong> {reports.rescheduledAppointments || 0}</Typography></Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ '&:hover': { color: '#1976d2' } }}>
                Financial Overview
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {selectedState || selectedCity
                    ? `Showing financial data for ${selectedCity ? `${selectedCity}, ` : ''}${selectedState || ''}${dateRange.start ? ` from ${dateRange.start.toLocaleDateString()} to ${dateRange.end?.toLocaleDateString() || 'today'}` : ''}`
                    : `Showing all financial data${dateRange.start ? ` from ${dateRange.start.toLocaleDateString()} to ${dateRange.end?.toLocaleDateString() || 'today'}` : ''}`}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">
                      <strong>Total Revenue:</strong> ₹{(financialData.reduce((sum, item) => sum + item.totalPaid, 0) || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">
                      <strong>Platform Earnings:</strong> ₹{(financialData.reduce((sum, item) => sum + item.platformEarning, 0) || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">
                      <strong>Doctor Earnings:</strong> ₹{(financialData.reduce((sum, item) => sum + item.finalDoctorEarning, 0) || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">
                      <strong>Pending Payouts:</strong> ₹{(reports.pendingPayouts || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">
                      <strong>Total Patient Commission:</strong> ₹{(financialData.reduce((sum, item) => sum + item.patientCommission, 0) || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">
                      <strong>Total Doctor Commission:</strong> ₹{(financialData.reduce((sum, item) => sum + item.doctorCommission, 0) || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">
                      <strong>Avg. Revenue per Appt:</strong> ₹{((financialData.reduce((sum, item) => sum + item.totalPaid, 0) || 0) / (reports.totalAppointments || 1)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">
                      <strong>Total Refunds:</strong> ₹{(reports.totalRefunds || 0).toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ height: 400, width: '100%', mb: 3 }}>
                  {financialLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <DataGrid
                      rows={financialData}
                      columns={financialColumns}
                      pageSize={5}
                      loading={financialLoading}
                      getRowId={(row) => row.doctorId + row.date}
                      sx={{ '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' } }}
                    />
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">Revenue Trend</Typography>
                    <Box sx={{ height: 300 }}>
                      <Bar
                        data={financialChartData.revenueTrend}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { position: 'top' }, title: { display: true, text: 'Revenue Breakdown Over Time' } },
                          scales: {
                            y: { beginAtZero: true, title: { display: true, text: 'Amount (₹)' } },
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">Commission Breakdown</Typography>
                    <Box sx={{ height: 300, maxWidth: 400, mx: 'auto' }}>
                      <Doughnut
                        data={financialChartData.commissionBreakdown}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { position: 'top' }, title: { display: true, text: 'Commission Distribution' } },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Revenue by Specialty</Typography>
                    <Box sx={{ height: 300 }}>
                      <Bar
                        data={financialChartData.specialtyRevenue}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { position: 'top' }, title: { display: true, text: 'Top Specialties by Revenue' } },
                          scales: {
                            y: { beginAtZero: true, title: { display: true, text: 'Revenue (₹)' } },
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ '&:hover': { color: '#1976d2' } }}>
                Verification & Compliance
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={4}><Typography variant="body1"><strong>Doctors Awaiting Verification:</strong> {reports.doctorsAwaitingVerification || 0}</Typography></Grid>
                <Grid item xs={12} lg={4}><Typography variant="body1"><strong>Rejected Doctor Applications:</strong> {reports.rejectedDoctorApplications || 0}</Typography></Grid>
                <Grid item xs={12} lg={4}><Typography variant="body1"><strong>Documents Pending Review:</strong> {reports.documentsPendingReview || 0}</Typography></Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Paper>

        {/* Doctor Management */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">Doctor Management</Typography>
            <IconButton onClick={() => handleExport(doctors, 'doctors_report.csv')}><DownloadIcon /></IconButton>
          </Box>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={doctors}
              columns={doctorColumns}
              pageSize={5}
              getRowId={(row) => row._id}
              sx={{ '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' } }}
            />
          </Box>
        </Paper>

        {/* Patient Management */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">Patient Management</Typography>
            <IconButton onClick={() => handleExport(patients, 'patients_report.csv')}><DownloadIcon /></IconButton>
          </Box>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={patients}
              columns={patientColumns}
              pageSize={5}
              getRowId={(row) => row._id}
              sx={{ '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' } }}
            />
          </Box>
        </Paper>

        {/* Appointment Management */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">Appointment Management</Typography>
            <IconButton onClick={() => handleExport(appointments, 'appointments_report.csv')}><DownloadIcon /></IconButton>
          </Box>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={appointments}
              columns={appointmentColumns}
              pageSize={5}
              getRowId={(row) => row.id}
              sx={{ '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' } }}
            />
          </Box>
        </Paper>

        {/* Withdrawal Requests */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">Withdrawal Requests</Typography>
            <IconButton onClick={() => handleExport(withdrawals, 'withdrawals_report.csv')}><DownloadIcon /></IconButton>
          </Box>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={withdrawals}
              columns={withdrawalColumns}
              pageSize={5}
              getRowId={(row) => row._id}
              sx={{ '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' } }}
            />
          </Box>
        </Paper>

        {/* Top Earning Specialties */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">Top Earning Specialties</Typography>
            <IconButton onClick={() => handleExport(topSpecialties, 'top_specialties_report.csv')}><DownloadIcon /></IconButton>
          </Box>
          <Box sx={{ height: 300, width: '100%' }}>
            <DataGrid
              rows={topSpecialties}
              columns={topSpecialtiesColumns}
              pageSize={3}
              getRowId={(row) => row.specialty} // Unique by specialty name
              sx={{ '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' } }}
            />
          </Box>
        </Paper>

        {/* Top Earning Doctors */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">Top Earning Doctors</Typography>
            <IconButton onClick={() => handleExport(topEarningDoctors, 'top_earning_doctors_report.csv')}><DownloadIcon /></IconButton>
          </Box>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={topEarningDoctors}
              columns={topEarningDoctorsColumns}
              pageSize={5}
              getRowId={(row) => row._id} // Use _id as unique identifier
              sx={{ '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' } }}
            />
          </Box>
        </Paper>

        {/* Graphs and Charts */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3, width: '100%' }}>
          <Typography variant="h6" gutterBottom color="primary">Graphs & Charts</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Typography variant="subtitle1">Monthly Revenue</Typography>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Bar data={revenueChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } }} height={300} />
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Typography variant="subtitle1">Appointment Trends</Typography>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Line data={appointmentTrendChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } }} height={300} />
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Typography variant="subtitle1">Revenue Growth Trend (Past 2 Years)</Typography>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Bar data={revenueGrowthTrendData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } }} height={300} />
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
            <Typography variant="subtitle1">Doctor Earnings Comparison (Top 10)</Typography>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Bar
                  data={doctorEarningsComparisonData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { 
                      legend: { position: 'top' },
                      title: { display: true, text: 'Top 10 Doctors by Net Earnings' } // Updated title for clarity
                    },
                    scales: {
                      y: { beginAtZero: true, title: { display: true, text: 'Net Earnings (₹)' } } // Updated Y-axis label
                    }
                  }}
                  height={300}
                />
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Typography variant="subtitle1">Refund & Cancellations Trend</Typography>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Line data={refundCancellationTrendData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } }} height={300} />
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Typography variant="subtitle1">Payment Mode Usage</Typography>
              <Box sx={{ height: 300, maxWidth: 400, mx: 'auto' }}>
                <Pie
                  data={paymentModeUsageData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'top' }, title: { display: true, text: 'Payment Method Distribution' } },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Geographical Performance (Top States)</Typography>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Bar data={geographicalPerformanceData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } }} height={300} />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ReportsAnalytics;